import { useState } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Check, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { type BookingPayment } from '@/hooks/useBookingPayments';
import { getBookingPaymentSummary } from '@/lib/booking/paymentSummary';

function fmtKr(ore: number): string {
  return Math.round(ore / 100).toLocaleString('sv-SE') + ' kr';
}

interface BookingPaymentSectionProps {
  payments: BookingPayment[];
  totalPaidOre: number;
  totalStayOre: number | null;
  prepaymentAmountOre: number | null;
  requiresPrepayment: boolean;
  isReserved: boolean;
  isCancelled: boolean;
  isPending: boolean;
  onAddPayment: (amountOre: number, paidAt: string, note: string | null) => Promise<void>;
  onDeletePayment: (paymentId: string) => Promise<void>;
  isAdding: boolean;
  isDeleting: boolean;
  legacyAmountPaidOre: number;
  legacyPaidAt: string | null;
}

export function BookingPaymentSection({
  payments, totalPaidOre, totalStayOre,
  prepaymentAmountOre, requiresPrepayment,
  isReserved, isCancelled, isPending,
  onAddPayment, onDeletePayment, isAdding, isDeleting,
  legacyAmountPaidOre, legacyPaidAt,
}: BookingPaymentSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [amountKr, setAmountKr] = useState('');
  const [note, setNote] = useState('');

  if (isCancelled || isPending) return null;

  const hasLegacyOnly = payments.length === 0 && legacyAmountPaidOre > 0;
  const effectiveTotalPaid = hasLegacyOnly ? legacyAmountPaidOre : totalPaidOre;

  const summary = getBookingPaymentSummary({
    totalPaidOre: effectiveTotalPaid,
    totalStayOre,
    prepaymentAmountOre,
    requiresPrepayment,
    paymentCount: payments.length + (hasLegacyOnly ? 1 : 0),
  });

  const openDialog = () => {
    const prefillOre = summary.suggestedNextPaymentOre;
    setAmountKr(prefillOre > 0 ? Math.round(prefillOre / 100).toString() : '');
    setNote('');
    setDialogOpen(true);
  };

  const handleAdd = async () => {
    const kr = parseInt(amountKr, 10);
    if (!kr || kr <= 0) return;
    await onAddPayment(kr * 100, new Date().toISOString(), note.trim() || null);
    setDialogOpen(false);
  };

  const paymentEntryCount = payments.length + (hasLegacyOnly ? 1 : 0);
  const totalOre = totalStayOre ?? 0;

  // Contextual subtitle
  const getSubtitle = (): string | null => {
    if (summary.isFullyPaid) return null;
    if (effectiveTotalPaid > 0 && requiresPrepayment && prepaymentAmountOre && effectiveTotalPaid >= prepaymentAmountOre) {
      return 'Förskott mottaget';
    }
    if (effectiveTotalPaid > 0) return 'Delvis betald';
    if (requiresPrepayment && isReserved) return 'Förskott inväntas';
    if (paymentEntryCount === 0) return 'Betalning hanteras utanför plattformen';
    return null;
  };

  const subtitle = getSubtitle();

  return (
    <>
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Betalning</h2>

        <div className="rounded-lg border border-border bg-card p-5 space-y-5">
          {/* A. Payment summary */}
          <div className="space-y-3">
            {summary.isFullyPaid ? (
              /* Fully paid — single calm line */
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10">
                  <Check className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="text-base font-semibold text-foreground">
                  Slutbetald · {fmtKr(effectiveTotalPaid)}
                </span>
              </div>
            ) : (
              /* Partial / unpaid — two-column summary */
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Betalt hittills</p>
                  <p className="text-lg font-semibold text-foreground tabular-nums">
                    {fmtKr(effectiveTotalPaid)}
                  </p>
                </div>
                {totalOre > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Återstår</p>
                    <p className="text-lg font-semibold text-foreground tabular-nums">
                      {fmtKr(summary.remainingOre)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>

          {/* B. Primary action */}
          {!summary.isFullyPaid && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={openDialog}>
              <Plus className="h-3.5 w-3.5" />
              Registrera betalning
            </Button>
          )}

          {/* C. Payment entries list */}
          {paymentEntryCount > 0 && (
            <div className="space-y-0 divide-y divide-border/60">
              {hasLegacyOnly && (
                <div className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                  <div className="text-sm">
                    <span className="font-medium text-foreground tabular-nums">
                      {fmtKr(legacyAmountPaidOre)}
                    </span>
                    {legacyPaidAt && (
                      <span className="text-muted-foreground ml-2">
                        · {format(new Date(legacyPaidAt), 'd MMM yyyy', { locale: sv })}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                  <div className="text-sm min-w-0">
                    <span className="font-medium text-foreground tabular-nums">
                      {fmtKr(p.amount_ore)}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      · {format(new Date(p.paid_at), 'd MMM yyyy', { locale: sv })}
                    </span>
                    {p.note && (
                      <span className="text-muted-foreground ml-2">· {p.note}</span>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="text-xs text-muted-foreground/60 hover:text-destructive transition-colors ml-3 shrink-0"
                        disabled={isDeleting}
                      >
                        Ta bort
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Ta bort betalning?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Betalningen på {fmtKr(p.amount_ore)} tas bort. Bokningens betalningsstatus uppdateras automatiskt.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDeletePayment(p.id)}>
                          Ta bort
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add payment dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Registrera betalning</DialogTitle>
            <DialogDescription>
              Ange hur mycket som har betalats för denna bokning.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="payment-amount">Belopp (kr)</Label>
              <Input
                id="payment-amount"
                type="number"
                min={1}
                placeholder="Belopp i kr"
                value={amountKr}
                onChange={(e) => setAmountKr(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-note">Anteckning (valfritt)</Label>
              <Textarea
                id="payment-note"
                placeholder="t.ex. Förskott via Swish"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="resize-none"
              />
            </div>
            {totalStayOre && totalStayOre > 0 && (
              <p className="text-xs text-muted-foreground">
                Totalt vistelsebelopp: {fmtKr(totalStayOre)}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">Avbryt</Button>
            </DialogClose>
            <Button onClick={handleAdd} disabled={isAdding}>
              {isAdding ? 'Registrerar…' : 'Bekräfta betalning'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
